import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { adminAPI, formatApiError } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Wallet, ArrowLeft, TrendUp, CurrencyDollar, Receipt, ChartBar, ArrowUpRight, PaperPlaneTilt, Storefront, HandCoins } from '@phosphor-icons/react';
import { toast } from 'sonner';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_moneyswap-9/artifacts/pvoha9c5_D%24.png";

export default function AdminAnalyticsPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      const res = await adminAPI.getAnalytics();
      setData(res.data);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

  if (user?.role !== 'admin') {
    return (
      <div className="min-h-screen bg-[#111111] flex items-center justify-center">
        <Card className="bg-[#1A1A1A] border border-[#333333] rounded-sm shadow-none max-w-md">
          <CardContent className="p-8 text-center">
            <p className="text-white text-lg font-bold mb-2">Access Denied</p>
            <p className="text-[#999999] mb-6">Admin access required.</p>
            <Link to="/dashboard">
              <Button className="bg-[#22C55E] hover:bg-[#16A34A] text-white rounded-sm">Back to Dashboard</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#111111] flex items-center justify-center">
        <div className="animate-pulse text-[#999999]">Loading analytics...</div>
      </div>
    );
  }

  const maxRevenue = data?.daily_revenue?.length
    ? Math.max(...data.daily_revenue.map(d => d.revenue), 0.01)
    : 1;

  const typeTotal = (data?.type_breakdown?.send || 0) + (data?.type_breakdown?.purchase || 0) + (data?.type_breakdown?.request || 0);

  return (
    <div className="min-h-screen bg-[#111111]">
      <header className="bg-[#111111]/70 backdrop-blur-xl border-b border-[#333333] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src={LOGO_URL} alt="Divispend" className="h-10 w-auto" />
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/admin/fees">
              <Button variant="ghost" className="text-[#999999] hover:text-white hover:bg-[#333333] rounded-sm text-sm">Fee Settings</Button>
            </Link>
            <Link to="/admin/users">
              <Button variant="ghost" className="text-[#999999] hover:text-white hover:bg-[#333333] rounded-sm text-sm">Users</Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <Link to="/dashboard" className="inline-flex items-center text-sm text-[#999999] hover:text-white mb-6">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Link>

        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 bg-[#22C55E]/10 rounded-sm flex items-center justify-center">
            <ChartBar className="w-6 h-6 text-[#22C55E]" weight="duotone" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-white" style={{ fontFamily: 'Cabinet Grotesk' }}>
              Fee Analytics
            </h1>
            <p className="text-sm text-[#999999]">Revenue insights and transaction trends</p>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Total Revenue', value: formatCurrency(data?.total_revenue || 0), icon: CurrencyDollar, accent: 'text-[#22C55E]', bg: 'bg-[#22C55E]/10' },
            { label: 'Projected Monthly', value: formatCurrency(data?.projected_monthly || 0), icon: TrendUp, accent: 'text-[#22C55E]', bg: 'bg-[#22C55E]/10' },
            { label: 'Total Fees Collected', value: data?.total_fee_count || 0, icon: Receipt, accent: 'text-[#F59E0B]', bg: 'bg-[#F59E0B]/10' },
            { label: 'Avg Fee / Transaction', value: formatCurrency(data?.avg_fee || 0), icon: ArrowUpRight, accent: 'text-[#22C55E]', bg: 'bg-[#22C55E]/10' },
          ].map((metric) => (
            <Card key={metric.label} className="bg-[#1A1A1A] border border-[#333333] rounded-sm shadow-none" data-testid={`metric-card-${metric.label}`}>
              <CardContent className="p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-9 h-9 ${metric.bg} rounded-sm flex items-center justify-center`}>
                    <metric.icon className={`w-5 h-5 ${metric.accent}`} weight="duotone" />
                  </div>
                  <span className="text-xs tracking-[0.1em] uppercase font-semibold text-[#999999]">{metric.label}</span>
                </div>
                <p className="text-2xl font-black tracking-tight text-white" style={{ fontFamily: 'Cabinet Grotesk' }}>
                  {metric.value}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Revenue Trend Chart */}
          <Card className="lg:col-span-2 bg-[#1A1A1A] border border-[#333333] rounded-sm shadow-none" data-testid="revenue-trend-card">
            <CardHeader className="p-6 pb-4">
              <CardTitle className="text-lg font-bold text-white" style={{ fontFamily: 'Cabinet Grotesk' }}>
                Revenue Trend (Last 30 Days)
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 pt-0">
              {data?.daily_revenue?.length > 0 ? (
                <div className="space-y-1">
                  <div className="flex items-end gap-1 h-48">
                    {data.daily_revenue.map((day, i) => {
                      const height = Math.max((day.revenue / maxRevenue) * 100, 4);
                      return (
                        <div key={day.date} className="flex-1 flex flex-col items-center justify-end group relative">
                          <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-[#333333] text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                            {day.date}: {formatCurrency(day.revenue)} ({day.count} fees)
                          </div>
                          <div
                            className="w-full bg-[#22C55E] rounded-t-sm transition-all duration-200 hover:bg-[#16A34A] min-w-[6px]"
                            style={{ height: `${height}%` }}
                            data-testid={`bar-${i}`}
                          />
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex justify-between text-xs text-[#999999] mt-2 pt-2 border-t border-[#333333]">
                    <span>{data.daily_revenue[0]?.date}</span>
                    <span>{data.daily_revenue[data.daily_revenue.length - 1]?.date}</span>
                  </div>
                </div>
              ) : (
                <div className="h-48 flex items-center justify-center text-[#999999]">
                  <div className="text-center">
                    <ChartBar className="w-12 h-12 mx-auto mb-2 text-[#333333]" />
                    <p>No fee data yet. Revenue will appear here as transactions occur.</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Revenue by Type */}
          <Card className="bg-[#1A1A1A] border border-[#333333] rounded-sm shadow-none" data-testid="type-breakdown-card">
            <CardHeader className="p-6 pb-4">
              <CardTitle className="text-lg font-bold text-white" style={{ fontFamily: 'Cabinet Grotesk' }}>
                Revenue by Type
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 pt-0">
              {typeTotal > 0 ? (
                <div className="space-y-4">
                  {[
                    { label: 'Money Transfers', value: data?.type_breakdown?.send || 0, icon: PaperPlaneTilt, color: '#22C55E' },
                    { label: 'Purchases', value: data?.type_breakdown?.purchase || 0, icon: Storefront, color: '#22C55E' },
                    { label: 'Payment Requests', value: data?.type_breakdown?.request || 0, icon: HandCoins, color: '#F59E0B' },
                  ].map((item) => {
                    const pct = typeTotal > 0 ? (item.value / typeTotal * 100) : 0;
                    return (
                      <div key={item.label} data-testid={`type-item-${item.label}`}>
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <item.icon className="w-4 h-4" style={{ color: item.color }} weight="duotone" />
                            <span className="text-sm text-[#999999]">{item.label}</span>
                          </div>
                          <span className="text-sm font-bold text-white">{formatCurrency(item.value)}</span>
                        </div>
                        <div className="w-full h-2 bg-[#333333] rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all duration-500"
                            style={{ width: `${pct}%`, backgroundColor: item.color }}
                          />
                        </div>
                        <p className="text-xs text-[#666666] mt-0.5">{pct.toFixed(1)}% of total</p>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="h-32 flex items-center justify-center text-[#999999] text-sm">
                  No fee revenue data yet
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Volume Summary */}
        <Card className="bg-[#1A1A1A] border border-[#333333] rounded-sm shadow-none" data-testid="volume-card">
          <CardHeader className="p-6 pb-4">
            <CardTitle className="text-lg font-bold text-white" style={{ fontFamily: 'Cabinet Grotesk' }}>
              Transaction Volume (Last 30 Days)
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 pt-0">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div>
                <p className="text-xs tracking-[0.1em] uppercase font-semibold text-[#999999] mb-1">Total Volume</p>
                <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Cabinet Grotesk' }}>
                  {formatCurrency(data?.total_volume || 0)}
                </p>
              </div>
              <div>
                <p className="text-xs tracking-[0.1em] uppercase font-semibold text-[#999999] mb-1">Transactions</p>
                <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Cabinet Grotesk' }}>
                  {data?.total_txn_count || 0}
                </p>
              </div>
              <div>
                <p className="text-xs tracking-[0.1em] uppercase font-semibold text-[#999999] mb-1">Effective Fee Rate</p>
                <p className="text-2xl font-bold text-[#22C55E]" style={{ fontFamily: 'Cabinet Grotesk' }}>
                  {data?.total_volume > 0 ? ((data.total_revenue / data.total_volume) * 100).toFixed(2) : '0.00'}%
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
